/**
 * useAllCategories Hook
 *
 * React Query hook for fetching ALL categories tree with long caching (5-10 min)
 * This is the single source of truth for category data on the frontend
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { fetchAllCategories } from '../../api/fetchAllCategories';
import type { CategoryTreeItem } from '../../types';

export interface UseAllCategoriesParams {
  includeInactive?: boolean;
  enabled?: boolean;
}

export interface UseAllCategoriesReturn {
  categoryTree: CategoryTreeItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  isRefetching: boolean;
}

/**
 * Query key factory for all categories
 */
export const allCategoriesKeys = {
  all: ['categories', 'tree', 'v2'] as const, // v2 to invalidate old cache
  withInactive: (includeInactive: boolean) =>
    [...allCategoriesKeys.all, includeInactive] as const,
};

/**
 * Hook for fetching all categories tree with React Query
 *
 * Features:
 * - Automatic caching (10 minutes stale time)
 * - Auto-refetch every 10 minutes
 * - Single source of truth for category data
 * - Background refetching
 *
 * @param params - Configuration for fetching categories
 * @returns Object with category tree, loading state, error, and refetch function
 */
export function useAllCategories(
  params: UseAllCategoriesParams = {},
): UseAllCategoriesReturn {
  const { includeInactive = false, enabled = true } = params;

  console.log('[useAllCategories] Hook params:', { includeInactive, enabled });

  const {
    data: categoryTree = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: allCategoriesKeys.withInactive(includeInactive),
    queryFn: async () => {
      console.log('[useAllCategories] queryFn starting...');
      try {
        const result = await fetchAllCategories({ includeInactive });
        console.log('[useAllCategories] queryFn result:', result);
        console.log('[useAllCategories] queryFn result length:', result?.length);
        return result;
      } catch (err) {
        console.error('[useAllCategories] queryFn error:', err);
        throw err;
      }
    },
    enabled,
    // Long cache time since categories don't change often
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (keep in cache even if unused)
    refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  console.log('[useAllCategories] Hook state:', {
    categoryTreeLength: categoryTree?.length,
    isLoading,
    error,
    isRefetching,
  });

  return {
    categoryTree,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refetch: () => {
      refetch();
    },
    isRefetching,
  };
}
